import React, { useRef, useEffect } from 'react';
import { tierLabel } from './recipeBookConstants';
import {
	recipeBookPanelClassName,
	recipeBookSelectClassName,
	recipeBookFilterLabelClassName,
	recipeBookFilterFieldClassName
} from './recipeBookStyles';

const TIER_OPTIONS = [
	{ value: '', label: 'All tiers' },
	{ value: 'health_first', label: 'Healthiest' },
	{ value: 'balanced', label: 'Balanced' },
	{ value: 'indulgent_last', label: 'Treat' }
];

const SORT_OPTIONS = [
	{ value: 'health', label: 'Healthiest' },
	{ value: 'popular', label: 'Most saved' },
	{ value: 'recent', label: 'Recently saved' },
	{ value: 'title', label: 'Title A–Z' }
];

export default function RecipeBookFilters({
	filters,
	onFilterChange,
	onClearFilters,
	hasActiveFilters,
	suggestions,
	suggestOpen,
	onSuggestOpenChange,
	onSelectSuggestion
}) {
	const wrapRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				onSuggestOpenChange(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [onSuggestOpenChange]);

	const suggestItems = [
		...(suggestions.titles || []).map((t) => ({ type: 'search', value: t, label: t })),
		...(suggestions.ingredients || []).map((i) => ({
			type: 'ingredient',
			value: i,
			label: `Ingredient: ${i}`
		}))
	];

	return (
		<div className={`${recipeBookPanelClassName} p-4 md:p-5`}>
			<div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
				<div
					className={recipeBookFilterFieldClassName}
					style={{ minWidth: '12.5rem' }}
					ref={wrapRef}
				>
					<label className={recipeBookFilterLabelClassName} htmlFor="recipe-book-search">
						Search
					</label>
					<div className="relative">
						<input
							id="recipe-book-search"
							type="text"
							className={recipeBookSelectClassName}
							placeholder="Dish, ingredient…"
							value={filters.search}
							onChange={(e) => {
								const value = e.target.value;
								onFilterChange('search', value);
								if (filters.ingredient && value !== '') {
									onFilterChange('ingredient', '');
								}
								onSuggestOpenChange(true);
							}}
							onFocus={() => onSuggestOpenChange(true)}
							autoComplete="off"
						/>
						{suggestOpen && suggestItems.length > 0 ? (
							<ul
								className="absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-lg py-1"
								role="listbox"
							>
								{suggestItems.map((item) => (
									<li key={`${item.type}-${item.value}`}>
										<button
											type="button"
											className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800"
											onClick={() => onSelectSuggestion(item)}
										>
											{item.label}
										</button>
									</li>
								))}
							</ul>
						) : null}
					</div>
				</div>

				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:flex lg:flex-1 lg:gap-3">
					<div className={recipeBookFilterFieldClassName}>
						<label className={recipeBookFilterLabelClassName}>Tier</label>
						<select
							className={recipeBookSelectClassName}
							value={filters.health_tier}
							onChange={(e) => onFilterChange('health_tier', e.target.value)}
						>
							{TIER_OPTIONS.map((opt) => (
								<option key={opt.value || 'all'} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>

					<div className={recipeBookFilterFieldClassName}>
						<label className={recipeBookFilterLabelClassName}>Sort</label>
						<select
							className={recipeBookSelectClassName}
							value={filters.sort}
							onChange={(e) => onFilterChange('sort', e.target.value)}
						>
							{SORT_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>
				</div>
			</div>

			{hasActiveFilters ? (
				<div className="flex flex-wrap items-center gap-2 pt-1">
					{filters.ingredient ? (
						<span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs text-blue-900 ring-1 ring-blue-200 dark:bg-blue-400 dark:text-blue-900 dark:ring-blue-500">
							Ingredient: {filters.ingredient}
							<button
								type="button"
								className="ml-0.5 text-blue-800 hover:text-blue-900 dark:text-blue-900 dark:hover:text-blue-800 font-bold leading-none"
								aria-label={`Remove ingredient filter ${filters.ingredient}`}
								onClick={() => onFilterChange('ingredient', '')}
							>
								×
							</button>
						</span>
					) : null}
					{filters.search?.trim() ? (
						<span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-dark-border">
							Search: {filters.search.trim()}
							<button
								type="button"
								className="ml-0.5 text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 font-bold leading-none"
								aria-label="Clear search"
								onClick={() => onFilterChange('search', '')}
							>
								×
							</button>
						</span>
					) : null}
					{filters.health_tier ? (
						<span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-dark-border">
							Tier: {tierLabel(filters.health_tier)}
							<button
								type="button"
								className="ml-0.5 text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 font-bold leading-none"
								aria-label="Clear tier filter"
								onClick={() => onFilterChange('health_tier', '')}
							>
								×
							</button>
						</span>
					) : null}
					<button
						type="button"
						className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
						onClick={onClearFilters}
					>
						Clear all
					</button>
				</div>
			) : null}
		</div>
	);
}
